import {
  Box,
  Container,
  makeStyles,
  Typography,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  InputAdornment,
} from "@material-ui/core";
import {
  InfoBox,
  InfoLine,
  GeneralModal,
} from "../components-molecules";
import {
  CancelButton,
  PrimaryButton,
  SubmitButton,
} from "../components-atoms";
import { HeaderWithButtons } from "../components-molecules";
import { EditShopModal } from "../components-molecules";
import { EditShopUserModal } from "../components-molecules/EditShopUserModal";
import { InviteShopUserModal } from "../components-molecules/InviteShopUserModal";
import EditIcon from "@material-ui/icons/Edit";
import ClearIcon from "@material-ui/icons/Clear";
import { ITheme } from "../constants/theme";
import { useEffect, useState, useCallback } from "react";
import { MuiPickersUtilsProvider, TimePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { enGB } from "date-fns/locale";
import moment from "moment";
import { 
  getShopDetails,
  editShopDetails, 
  inviteShopUser,
  ShopDetailsResponseModel,
  EditShopDetailsRequestModel,
  ShopUserDetails,
  InviteShopUserData,
  InviteShopUserResponseModel,
  ShopWorkingDayAndHourRequest,
  setShopWorkingHours
} from "../redux/actions/shopDetails";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: ITheme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    maxWidth: "840px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  detailsContainer: {
    marginTop: "24px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  shopImageContainer: {
    width: 200,
    height: 150,
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    borderRadius: 8,
    marginBottom: 16,
  },
  statusChip: {
    fontSize: '0.7rem',
    height: 24,
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
  },
  addressContainer: {
    marginTop: 8,
    paddingBottom: 16,
  },
  usersContainer: {
    marginTop: 16,
  },
  usersSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    marginTop: 24,
    paddingLeft: 0,
    paddingRight: 0,
  },
  usersSectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  userList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
    marginTop: 16,
    paddingBottom: 24,
  },
  userContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 20px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 12,
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  userInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
  },
  userNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  userName: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  userEmail: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },
  userDetails: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  userStatusContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  inviteButton: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '8px 16px',
    borderRadius: 8,
  },
  uMarginTop0: {
    marginTop: 0,
  },
  clearButton: {
    width: 24,
    height: 24,
    "&:hover": { backgroundColor: "transparent" },
  },
  clearIcon: {
    width: 18,
    height: 18,
  },
  workingHoursModalContent: {
    maxHeight: '60vh',
    overflowY: 'auto',
    padding: '16px 24px',
  },
  workingHoursModalButtons: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    borderTop: '1px solid #e0e0e0',
    padding: '16px 24px',
  },
  dayRow: {
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid transparent',
    transition: 'all 0.2s ease-in-out',
  },
  dayRowDeleted: {
    opacity: 0.5,
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
  },
}));

export const AdminShopDetailsPage = () => {
  const params: any = useParams();
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation();

  const [shop, setShop] = useState<ShopDetailsResponseModel>();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  
  // User management state
  const [editUserModalVisible, setEditUserModalVisible] = useState(false);
  const [inviteUserModalVisible, setInviteUserModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ShopUserDetails | null>(null);
  const [inviteUserLoading, setInviteUserLoading] = useState(false);
  const [inviteUserSuccessModalVisible, setInviteUserSuccessModalVisible] = useState(false);
  const [inviteUserSuccessModalDescription, setInviteUserSuccessModalDescription] = useState('');
  // Working hours state
  const [workingHours, setWorkingHours] = useState<Array<{
    Day?: number;
    Id?: string;
    StartTime?: Date | string;
    EndTime?: Date | string;
    ShouldBeDeleted?: boolean;
  }>>([]);
  const [editWorkingHoursModalVisible, setEditWorkingHoursModalVisible] = useState(false);
  const [editWorkingHoursLoading, setEditWorkingHoursLoading] = useState(false);

  const getShopData = useCallback(() => {
    setInitialLoading(true);
    getShopDetails(
      params.merchantId,
      params.id,
      (res) => {
        setShop(res);
        setInitialLoading(false);
      },
      (err) => {
        setInitialLoading(false);
      }
    );
  }, [params.id, params.merchantId]);

  useEffect(() => {
    getShopData();
  }, [getShopData]);

  // Initialize working hours when shop data is loaded
  useEffect(() => {
    if (shop?.WorkingHours) {
      const sortedHours = [...shop.WorkingHours].sort((a, b) => a.Day - b.Day).map(wh => ({
        ...wh,
        ShouldBeDeleted: false
      }));
      setWorkingHours(sortedHours);
    }
  }, [shop]);

  const handleEdit = () => {
    setEditModalVisible(true);
  };

  const handleEditSubmit = (data: EditShopDetailsRequestModel) => {
    if (!shop?.Id) return;
    
    setEditLoading(true);
    editShopDetails(
      params.merchantId,
      shop.Id,
      data,
      (response) => {
        setEditLoading(false);
        setEditModalVisible(false);
        // Refresh the shop data
        getShopData();
      },
      (error) => {
        setEditLoading(false);
        // TODO: Show error message to user
      }
    );
  };

  const handleEditModalClose = () => {
    setEditModalVisible(false);
  };

  // User management handlers
  const handleEditUser = (user: ShopUserDetails) => {
    setSelectedUser(user);
    setEditUserModalVisible(true);
  };

  const handleEditUserModalClose = () => {
    setEditUserModalVisible(false);
    setSelectedUser(null);
  };

  const handleEditUserSuccess = () => {
    getShopData(); // Refresh the data
  };

  const handleInviteUser = () => {
    setInviteUserModalVisible(true);
  };

  const handleInviteUserModalClose = () => {
    setInviteUserModalVisible(false);
  };

  const handleInviteUserSubmit = (data: InviteShopUserData) => {
    setInviteUserLoading(true);
    inviteShopUser(
      params.merchantId,
      params.id,
      data,
      (response: InviteShopUserResponseModel) => {
        setInviteUserLoading(false);
        setInviteUserModalVisible(false);
        setInviteUserSuccessModalDescription(`${t('adminShopDetails.userAddedDescription')} ${response.Password}`);
        setInviteUserSuccessModalVisible(true);
        // Refresh the shop data to show the new user
        getShopData();
      },
      (error) => {
        setInviteUserLoading(false);
        // TODO: Show error message to user
      }
    );
  };

  const handleInviteUserSuccessModalClose = () => {
    setInviteUserSuccessModalVisible(false);
    setInviteUserSuccessModalDescription('');
  };

  // Working hours handlers
  const handleEditWorkingHours = () => {
    setEditWorkingHoursModalVisible(true);
  };

  const handleEditWorkingHoursModalClose = () => {
    setEditWorkingHoursModalVisible(false);
  };

  const handleEditWorkingHoursSubmit = () => {
    if (!shop?.Id) return;
    
    setEditWorkingHoursLoading(true);
    
    // Convert working hours to the format expected by the API
    const workingHoursData = workingHours
      .filter(wh => wh.Day !== undefined) // Include all days that have been touched
      .map(wh => ({
        Id: wh.Id,
        Day: wh.Day!,
        StartHour: wh.StartTime ? moment(wh.StartTime).hour() : 0,
        StartMinute: wh.StartTime ? moment(wh.StartTime).minute() : 0,
        EndHour: wh.EndTime ? moment(wh.EndTime).hour() : 0,
        EndMinute: wh.EndTime ? moment(wh.EndTime).minute() : 0,
        ShouldBeDeleted: wh.ShouldBeDeleted || false,
      }));

    const editData: ShopWorkingDayAndHourRequest = {
      WorkingHours: workingHoursData
    };

    setShopWorkingHours(
      params.merchantId,
      shop.Id,
      editData,
      (response) => {
        setEditWorkingHoursLoading(false);
        setEditWorkingHoursModalVisible(false);
        // Refresh the shop data
        getShopData();
      },
      (error) => {
        setEditWorkingHoursLoading(false);
        // TODO: Show error message to user
      }
    );
  };

  const renderWorkingHoursForm = () => {
    const timePickerValue = (value: string | null | Date | undefined) => {
      if (value) {
        // Treat as simple hour/minute - no timezone conversion
        return moment(value).toDate();
      }
      return null;
    };

    const dateClearClicked = (e: any, index: number, time: "start" | "end") => {
      e.stopPropagation();
      e.preventDefault();
      let temp = [...workingHours];
      const listIndex = temp.findIndex((item) => item?.Day === index);

      if (temp?.[listIndex]) {
        switch (time) {
          case "start":
            temp[listIndex].StartTime = undefined;
            if (!temp[listIndex]?.EndTime || temp[listIndex]?.EndTime === undefined) {
              temp = temp.filter((item) => item?.Day !== index);
            }
            break;
          case "end":
            temp[listIndex].EndTime = undefined;
            if (!temp[listIndex]?.StartTime || temp[listIndex]?.StartTime === undefined) {
              temp = temp.filter((item) => item?.Day !== index);
            }
            break;
          default:
            break;
        }
      } else {
        temp[listIndex] = { Day: index, StartTime: undefined, EndTime: undefined };
        switch (time) {
          case "start":
            temp[listIndex].StartTime = undefined;
            break;
          case "end":
            temp[listIndex].EndTime = undefined;
            break;
          default:
            break;
        }
      }

      setWorkingHours(temp);
    };

    const getWorkingHourStartTime = (index: number) => {
      const result = workingHours.filter((item) => item?.Day === index);
      return result[0]?.StartTime ? result[0].StartTime : undefined;
    };

    const getWorkingHourEndTime = (index: number) => {
      const result = workingHours.filter((item) => item?.Day === index);
      return result[0]?.EndTime ? result[0].EndTime : undefined;
    };

    const handleDeleteDay = (dayIndex: number) => {
      const temp = [...workingHours];
      const existingIndex = temp.findIndex(item => item.Day === dayIndex);
      
      if (existingIndex >= 0) {
        // Mark for deletion
        temp[existingIndex] = { ...temp[existingIndex], ShouldBeDeleted: true };
      } else {
        // Add new item marked for deletion
        temp.push({ Day: dayIndex, ShouldBeDeleted: true });
      }
      setWorkingHours(temp);
    };

    const handleRestoreDay = (dayIndex: number) => {
      const temp = [...workingHours];
      const existingIndex = temp.findIndex(item => item.Day === dayIndex);
      
      if (existingIndex >= 0) {
        temp[existingIndex] = { ...temp[existingIndex], ShouldBeDeleted: false };
      }
      setWorkingHours(temp);
    };

    const isDayMarkedForDeletion = (dayIndex: number) => {
      const dayData = workingHours.find(item => item.Day === dayIndex);
      return dayData?.ShouldBeDeleted || false;
    };

    return [
      "Sunday",
      "Monday",
      "Tuesday", 
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ].map((item: any, index: number) => {
      const dayIndex = index; // Sunday = 0, Monday = 1, etc.
      const isMarkedForDeletion = isDayMarkedForDeletion(dayIndex);
      
      return (
        <Grid
          container
          spacing={4}
          key={index}
          className={`${index === 0 ? classes.uMarginTop0 : ""} ${classes.dayRow} ${isMarkedForDeletion ? classes.dayRowDeleted : ""}`}
        >
          <Grid item xs={10}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TimePicker
                  variant="dialog"
                  value={timePickerValue(getWorkingHourStartTime(dayIndex))}
                  inputVariant="outlined"
                  disabled={isMarkedForDeletion}
                  ampm={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position={"end"}>
                        {timePickerValue(getWorkingHourStartTime(dayIndex)) ? (
                          <IconButton
                            data-automation="clear"
                            className={classes.clearButton}
                            onClick={(e) => {
                              dateClearClicked(e, dayIndex, "start");
                            }}
                          >
                            <ClearIcon
                              htmlColor="rgba(0,0,0,0.54)"
                              className={classes.clearIcon}
                            />
                          </IconButton>
                        ) : (
                          <div className={classes.clearButton}></div>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  label={item + " from"}
                  onChange={(val: any) => {
                    // Store as-is, no timezone conversion
                    const temp = [...workingHours];
                    const existingIndex = temp.findIndex(item => item.Day === dayIndex);
                    
                    if (existingIndex >= 0) {
                      temp[existingIndex] = { ...temp[existingIndex], StartTime: val, ShouldBeDeleted: false };
                    } else {
                      temp.push({ Day: dayIndex, StartTime: val, EndTime: undefined, ShouldBeDeleted: false });
                    }
                    setWorkingHours(temp);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  variant="dialog"
                  inputVariant="outlined"
                  label={item + " to"}
                  value={timePickerValue(getWorkingHourEndTime(dayIndex))}
                  disabled={isMarkedForDeletion}
                  ampm={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position={"end"}>
                        {timePickerValue(getWorkingHourEndTime(dayIndex)) ? (
                          <IconButton
                            data-automation="clear"
                            className={classes.clearButton}
                            onClick={(e) => dateClearClicked(e, dayIndex, "end")}
                          >
                            <ClearIcon
                              htmlColor="rgba(0,0,0,0.54)"
                              className={classes.clearIcon}
                            />
                          </IconButton>
                        ) : (
                          <div className={classes.clearButton}></div>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  onChange={(val: any) => {
                    // Store as-is, no timezone conversion
                    const temp = [...workingHours];
                    const existingIndex = temp.findIndex(item => item.Day === dayIndex);
                    
                    if (existingIndex >= 0) {
                      temp[existingIndex] = { ...temp[existingIndex], EndTime: val, ShouldBeDeleted: false };
                    } else {
                      temp.push({ Day: dayIndex, StartTime: undefined, EndTime: val, ShouldBeDeleted: false });
                    }
                    setWorkingHours(temp);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isMarkedForDeletion ? (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handleRestoreDay(dayIndex)}
                style={{ fontSize: '0.75rem' }}
              >
                {t('adminShopDetails.restore')}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handleDeleteDay(dayIndex)}
                style={{ fontSize: '0.75rem' }}
              >
                {t('adminShopDetails.delete')}
              </Button>
            )}
          </Grid>
        </Grid>
      );
    });
  };

  const renderHeader = () => {
    return (
      <HeaderWithButtons 
        title={`${t('adminShopDetails.shop')}: ${shop?.Name || ''}`}
        handleBack={() => history.goBack()}
      >
        <HeaderWithButtons.ButtonArea>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleEdit}
            startIcon={<EditIcon />}
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              padding: '8px 16px',
              borderRadius: 8,
            }}
          >
            {t('adminShopDetails.editShop')}
          </Button>
        </HeaderWithButtons.ButtonArea>
      </HeaderWithButtons>
    );
  };

  const renderUser = (user: ShopUserDetails, index: number) => {
    return (
      <Card 
        key={index} 
        className={classes.userContainer}
      >
        <CardContent style={{ padding: '16px' }}>
          <Box className={classes.userInfoContainer}>
            <Box className={classes.userNameContainer}>
              <Typography className={classes.userName}>
                {user.FirstName} {user.LastName}
              </Typography>
            </Box>
            
            <Typography className={classes.userEmail}>
              {user.Email?.Email || 'No email'}
            </Typography>
            
            {user.PhoneNumber?.PhoneNumber && (
              <Typography className={classes.userDetails}>
                {user.PhoneNumber.PhoneNumber}
              </Typography>
            )}
            
            <Typography className={classes.userDetails}>
              {t('adminShopDetails.emailNotifications')}: {user.Email?.NotificationsEnabled ? t('adminShopDetails.enabled') : t('adminShopDetails.disabled')}
            </Typography>
            
            <Typography className={classes.userDetails}>
              {t('adminShopDetails.phoneNotifications')}: {user.PhoneNumber?.NotificationsEnabled ? t('adminShopDetails.enabled') : t('adminShopDetails.disabled')}
            </Typography>
          </Box>
          
          <Box className={classes.userStatusContainer}>
            <IconButton
              size="small"
              onClick={() => handleEditUser(user)}
              style={{ 
                marginRight: 8,
                padding: 4
              }}
            >
              <EditIcon style={{ fontSize: 16, color: '#666' }} />
            </IconButton>
            <Chip
              label={t('adminShopDetails.active')}
              color="primary"
              size="small"
              className={classes.statusChip}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderShopImage = () => {
    if (!shop?.ShopImageUrl) {
      return (
        <Box className={classes.shopImageContainer} style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}>
          <Typography variant="body2" color="textSecondary">
            {t('adminShopDetails.noImage')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box 
        className={classes.shopImageContainer}
        style={{ backgroundImage: `url('${shop.ShopImageUrl}')` }}
      />
    );
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={enGB}>
      <Box className={classes.container}>
      <Container className={classes.root}>
        {initialLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {renderHeader()}
            
            <Container className={classes.detailsContainer}>
              <InfoBox title={t('adminShopDetails.shopInformation')}>
                <InfoLine
                  name={t('adminShopDetails.shopName')}
                  value={shop?.Name || "-"}
                />
                <InfoLine
                  name={t('adminShopDetails.phoneNumber')}
                  value={shop?.PhoneNumber || "-"}
                />
                <InfoLine
                  name={t('adminShopDetails.infoEmail')}
                  value={shop?.InfoEmail || "-"}
                />
                <InfoLine
                  name={t('adminShopDetails.businessNameNash')}
                  value={shop?.OrkestroDisplayName || "-"}
                />
                <InfoLine
                  name={t('adminShopDetails.tkdEnabled')}
                  value={shop?.TkdEnabled ? t('adminShopDetails.yes') : t('adminShopDetails.no')}
                />
                <InfoLine
                  name={t('adminShopDetails.marketplaceEnabled')}
                  value={shop?.MarketplaceEnabled ? t('adminShopDetails.yes') : t('adminShopDetails.no')}
                />
                 <InfoLine
                  name={t('adminShopDetails.shopImage')}
                  value={renderShopImage()}
                />
              </InfoBox>

              <InfoBox title={t('adminShopDetails.address')}>
                {shop?.Address ? (
                  <Box className={classes.addressContainer}>
                    {shop.Address.Line1 && <Typography variant="body2">{shop.Address.Line1}</Typography>}
                    {shop.Address.Line2 && <Typography variant="body2">{shop.Address.Line2}</Typography>}
                    {shop.Address.Line3 && <Typography variant="body2">{shop.Address.Line3}</Typography>}
                    <Typography variant="body2">
                      {[shop.Address.City, shop.Address.County, shop.Address.PostalCode]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                    {shop.Address.Lat && shop.Address.Long && (
                      <Typography variant="body2" color="textSecondary">
                        {t('adminShopDetails.coordinates')}: {shop.Address.Lat.toFixed(6)}, {shop.Address.Long.toFixed(6)}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2">-</Typography>
                )}
              </InfoBox>

              {shop?.MarketplaceEnabled && (
              <InfoBox title={t('adminShopDetails.workingHours')}>
                <InfoLine
                  name=""
                  value={t('adminShopDetails.workingHoursDescription')}
                  onClick={handleEditWorkingHours}
                  clickable
                >
                  <InfoLine.ArrowArea></InfoLine.ArrowArea>
                </InfoLine>
                
                {workingHours && workingHours.length > 0 ? (
                  workingHours
                    .filter(wh => !wh.ShouldBeDeleted) // Only show non-deleted hours
                    .sort((a, b) => a.Day! - b.Day!) // Sort by day number (Sunday=0, Monday=1, etc.)
                    .map((wh, index) => {
                      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      const dayName = dayNames[wh.Day!];
                      // Display times as-is, no timezone conversion
                      const startTime = wh.StartTime ? moment(wh.StartTime).format("HH:mm") : '';
                      const endTime = wh.EndTime ? moment(wh.EndTime).format("HH:mm") : '';
                      
                      return (
                        <InfoLine
                          key={index}
                          name={dayName}
                          value={`${startTime} - ${endTime}`}
                          hasBorder={index !== workingHours.filter(wh => !wh.ShouldBeDeleted).length - 1}
                        />
                      );
                    })
                ) : (
                    <Typography variant="body2">{t('adminShopDetails.noWorkingHoursSet')}</Typography>
                  )}
                </InfoBox>
              )}

              {shop && (
                <Box>
                  <Box className={classes.usersSectionHeader}>
                    <Typography variant="h6" className={classes.usersSectionTitle}>
                      {t('adminShopDetails.users')} ({shop.Users?.length || 0})
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleInviteUser}
                      className={classes.inviteButton}
                    >
                      {t('adminShopDetails.addUser')}
                    </Button>
                  </Box>
                  {shop.Users && Array.isArray(shop.Users) && shop.Users.length > 0 && (
                    <Box className={classes.userList}>
                      {shop.Users.map(renderUser)}
                    </Box>
                  )}
                </Box>
              )}

            </Container>
          </>
        )}
      </Container>
      
      <EditShopModal
        visible={editModalVisible}
        onClose={handleEditModalClose}
        onSubmit={handleEditSubmit}
        shop={shop}
        loading={editLoading}
      />
      
      <EditShopUserModal
        visible={editUserModalVisible}
        onClose={handleEditUserModalClose}
        onSuccess={handleEditUserSuccess}
        user={selectedUser}
        merchantId={params.merchantId}
        shopId={params.id}
      />
      
      <InviteShopUserModal
        visible={inviteUserModalVisible}
        onClose={handleInviteUserModalClose}
        onSubmit={handleInviteUserSubmit}
        loading={inviteUserLoading}
      />

      <GeneralModal
        visible={inviteUserSuccessModalVisible}
        title={t('adminShopDetails.userAdded')}
        description={inviteUserSuccessModalDescription}
        onClose={handleInviteUserSuccessModalClose}
      >
        <GeneralModal.ButtonArea>
          <PrimaryButton
            onClick={handleInviteUserSuccessModalClose}
            title="OK"
          />
        </GeneralModal.ButtonArea>
      </GeneralModal>
      
      {/* Working Hours Modal */}
      <GeneralModal
        visible={editWorkingHoursModalVisible}
        title={t('adminShopDetails.workingHoursModalTitle')}
        description={t('adminShopDetails.workingHoursModalDescription')}
        onClose={handleEditWorkingHoursModalClose}
      >
        <GeneralModal.ContentArea>
          <Box style={{ 
            maxHeight: '60vh', 
            overflowY: 'auto',
            padding: '16px 24px'
          }}>
            <Typography variant="body2" component="span" style={{ marginBottom: 16, display: 'block' }}>
              {t('adminShopDetails.workingHoursModalInstructions')}
            </Typography>
            <Typography variant="body2" component="span" style={{ marginBottom: 16, display: 'block', color: '#666' }}>
              {t('adminShopDetails.workingHoursModalDeleteInstructions')}
            </Typography>
            {renderWorkingHoursForm()}
          </Box>
        </GeneralModal.ContentArea>
        <GeneralModal.ButtonArea>
          <Box style={{ 
            position: 'sticky', 
            bottom: 0, 
            backgroundColor: 'white', 
            borderTop: '1px solid #e0e0e0',
            padding: '16px 24px'
          }}>
            <CancelButton onClick={handleEditWorkingHoursModalClose} />
            <SubmitButton
              disabled={editWorkingHoursLoading}
              onClick={handleEditWorkingHoursSubmit}
            />
          </Box>
        </GeneralModal.ButtonArea>
      </GeneralModal>
      </Box>
    </MuiPickersUtilsProvider>
  );
};
